import { Buildings, Question, Lightbulb, Gear, ArrowRight, Sparkle, TrendUp, TrendDown, Scales, ShoppingCart, Handshake, Factory, Users, CurrencyCircleDollar, Package, ChartBar } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

function Section({ icon: Icon, title, subtitle, tone, children, testid }) {
  const tones = {
    amber: "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/25 bg-emerald-500/5",
    blue: "border-blue-500/25 bg-blue-500/5",
  };
  const iconTones = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  };
  return (
    <section className={`rounded-sm border ${tones[tone]} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${iconTones[tone]}`}>
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-50">{title}</h2>
          {subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Pill({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
      {Icon && <Icon size={14} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-zinc-100 mb-0.5">{title}</p>
        <p className="text-[11px] text-zinc-400 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// Simple supply/demand SVG diagram
function SupplyDemandChart() {
  return (
    <svg viewBox="0 0 320 200" className="w-full max-w-md mx-auto" aria-label="Supply and demand diagram">
      {/* Axes */}
      <line x1="40" y1="170" x2="290" y2="170" stroke="#52525b" strokeWidth="1" />
      <line x1="40" y1="20" x2="40" y2="170" stroke="#52525b" strokeWidth="1" />
      <text x="290" y="185" fontSize="9" fill="#71717a" textAnchor="end">Quantity →</text>
      <text x="16" y="25" fontSize="9" fill="#71717a">Price</text>
      {/* Demand curve — downward slope (red) */}
      <line x1="50" y1="35" x2="280" y2="160" stroke="#ef4444" strokeWidth="2" />
      <text x="260" y="40" fontSize="10" fill="#ef4444" fontWeight="bold">Demand</text>
      {/* Supply curve — upward slope (green) */}
      <line x1="50" y1="160" x2="280" y2="35" stroke="#10b981" strokeWidth="2" />
      <text x="260" y="160" fontSize="10" fill="#10b981" fontWeight="bold">Supply</text>
      {/* Equilibrium point */}
      <circle cx="165" cy="97" r="4" fill="#f59e0b" />
      <text x="172" y="92" fontSize="9" fill="#fbbf24" fontWeight="bold">Equilibrium</text>
      <line x1="165" y1="97" x2="165" y2="170" stroke="#f59e0b" strokeWidth="0.5" strokeDasharray="2 2" />
      <line x1="40" y1="97" x2="165" y2="97" stroke="#f59e0b" strokeWidth="0.5" strokeDasharray="2 2" />
      <text x="166" y="182" fontSize="8" fill="#a1a1aa">Q*</text>
      <text x="26" y="100" fontSize="8" fill="#a1a1aa">P*</text>
    </svg>
  );
}

export default function Microeconomics() {
  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1100px] overflow-x-hidden" data-testid="micro-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/30 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <Buildings size={22} weight="bold" className="text-amber-400" />
          <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/15 text-amber-300">THE DETAILS</Badge>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">Microeconomics</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl">
          Zoom in. Macro is the weather; <span className="text-amber-300 font-medium">micro is the individuals</span> — single companies, single products,
          single buyers and sellers. This is how you understand a stock, not a market.
        </p>
      </div>

      {/* WHAT */}
      <Section icon={Question} title="What is microeconomics?" subtitle="The behavior of individuals, households, and single firms" tone="amber" testid="micro-what">
        <p>
          Microeconomics studies the <span className="text-amber-300 font-medium">small decisions</span> that add up to make the whole economy move:
          one person choosing what to buy, one company deciding what to charge, one worker deciding whether to take a job.
        </p>
        <p>
          <strong className="text-zinc-100">Macro looks at the forest. Micro looks at the tree.</strong> Both matter. You need both to actually understand markets.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <Pill icon={ShoppingCart} title="Consumers" body="People choosing what to buy at what price." />
          <Pill icon={Factory} title="Producers" body="Companies deciding what to make and at what cost." />
          <Pill icon={Handshake} title="Markets" body="Where buyers &amp; sellers meet and agree on a price." />
        </div>
      </Section>

      {/* WHY */}
      <Section icon={Lightbulb} title="Why should you care?" subtitle="Every stock price is a micro story" tone="emerald" testid="micro-why">
        <p>
          A stock's price reflects <strong className="text-zinc-100">one company's</strong> micro-economics —
          whether they can charge more, sell more, keep costs low, and stop competitors from eating their lunch.
        </p>

        <ol className="space-y-2 pl-1">
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">01</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Pricing power.</strong> Can a company raise prices without losing customers? Apple can. Dollar stores can't. That's why one trades at 30× earnings and the other at 10×.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">02</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Supply crunches pump prices.</strong> When there's less of something people still want (oil, a hot GPU, a meme coin), prices spike. Bitcoin's 21M cap is a supply-side bet.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">03</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Competition kills margins.</strong> When many companies sell the same thing, they undercut each other. Razor-thin margins = bad investment. Monopolies (or network-effect winners) are where wealth is made.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">04</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Incentives drive behavior.</strong> Tax a thing, you get less of it. Subsidize a thing, you get more. EV credits → more EVs sold. Rate cuts → more home buying.</span>
          </li>
        </ol>
      </Section>

      {/* HOW */}
      <Section icon={Gear} title="The 5 micro concepts that actually matter" subtitle="Everything else builds on these" tone="blue" testid="micro-how">
        {/* Supply & Demand */}
        <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">1</span>
            <Scales size={14} weight="bold" className="text-blue-400" />
            <p className="text-sm font-medium text-zinc-100">Supply &amp; Demand</p>
          </div>
          <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
            The <strong className="text-zinc-200">single most important idea in economics</strong>. Prices are set where buyers and sellers meet.
            More buyers than stuff → price up. More stuff than buyers → price down.
          </p>
          <div className="p-3 rounded-sm bg-zinc-950/60 border border-zinc-800" data-testid="supply-demand-chart">
            <SupplyDemandChart />
            <p className="text-[10px] text-zinc-500 text-center mt-2">
              Where the red (demand) and green (supply) lines cross = the <span className="text-amber-400">equilibrium price</span>.
              That's the price the market naturally settles at.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">2</span>
              <TrendUp size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Elasticity</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              How sensitive buyers are to price changes. If bread doubles, people still buy it (inelastic). If a luxury bag doubles, sales collapse (elastic).
              <br/><span className="text-amber-300">Pros:</span> inelastic = durable business (utilities, medicine). Elastic = risky in downturns.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">3</span>
              <Package size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Opportunity Cost</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              The value of what you give up by choosing one option. Buying Bitcoin = not buying stocks. Working a shift = not sleeping. Every decision has a cost that's <em>not</em> the sticker price.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">4</span>
              <ChartBar size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Marginal Thinking</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Decisions happen at the margin — the <em>next</em> one unit. Should I make one more widget? Only if what I charge beats what it costs me. This is how businesses set prices.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">5</span>
              <Factory size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Market Structure</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Who's competing? <strong className="text-zinc-200">Monopoly</strong> (1 seller, rare &amp; powerful), <strong className="text-zinc-200">oligopoly</strong> (few big players, e.g. US airlines), or <strong className="text-zinc-200">perfect competition</strong> (many equal players, low margins). Knowing which = knowing how hard it is to make money.
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={14} weight="fill" className="text-blue-400" />
            <p className="text-xs font-medium text-blue-200">The micro cheat sheet</p>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            Before buying a stock or token, ask: <strong className="text-zinc-100">what's the supply, what's the demand, who else is competing, and how price-sensitive are the buyers?</strong>
            {" "}If you can answer those 4 for any asset, you're already ahead of most retail traders.
          </p>
        </div>
      </Section>

      {/* Next steps */}
      <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 p-5 sm:p-6" data-testid="micro-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100">Put it into practice</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Pill icon={TrendUp} title="Trading tab" body="Now apply these to actual charts — support, resistance, and volume." />
          <Pill icon={CurrencyCircleDollar} title="Compound Interest" body="The quiet math that turns small savings into big wealth — powered by microeconomic returns over time." />
        </div>
      </div>
    </div>
  );
}
